import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { SearchPalette } from './components/SearchPalette';

interface Ctx {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const SearchPaletteContext = createContext<Ctx | null>(null);

export function useSearchPalette(): Ctx {
  const ctx = useContext(SearchPaletteContext);
  if (!ctx) throw new Error('useSearchPalette must be used inside SearchPaletteProvider');
  return ctx;
}

export function SearchPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Cmd+K (mac) / Ctrl+K (win/linux) → toggle. También `/` como en GitHub,
  // pero solo cuando el foco NO está en un input/textarea.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
        e.preventDefault();
        open();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle, open]);

  return (
    <SearchPaletteContext.Provider value={{ open, close, toggle }}>
      {children}
      <SearchPalette open={isOpen} onClose={close} />
    </SearchPaletteContext.Provider>
  );
}
